/**
 * 小屏笔记本整体缩放适配
 *
 * 当浏览器窗口宽度 <= SCALE_BREAKPOINT 或高度 <= SCALE_HEIGHT_BREAKPOINT 时，
 * 将整站缩小到 SCALE_RATIO（80%），让一屏能塞下更多内容；否则恢复 100%。
 *
 * 采用 CSS `zoom` 而非 `transform: scale`：zoom 是真正的缩放，会自动影响布局、
 * 字体、滚动条与定位，且不会破坏 Element Plus 挂到 body 的弹层（弹窗/下拉/气泡）定位。
 * 作用在 <html> 上，整站（含 body 上的弹窗、Toast）一并生效。
 */

/**
 * 触发缩放的窗口宽度阈值（含）：覆盖 1366/1440、MacBook 14 寸（1512）
 * 及 1920 分辨率 + 系统缩放 125%（1536）的笔记本，1920 及以上大屏不缩
 */
const SCALE_BREAKPOINT = 1536;

/**
 * 触发缩放的窗口高度阈值（含）：宽度超过阈值但可视高度不足的屏幕（如 2.2K + 150% 缩放）同样需要缩放
 */
const SCALE_HEIGHT_BREAKPOINT = 800;

/** 小屏时的缩放比例 */
const SCALE_RATIO = 0.8;

/** resize 防抖延迟（毫秒） */
const RESIZE_DEBOUNCE = 150;

/**
 * 小屏紧凑模式标记类，挂在 <html> 上，与 zoom 完全同步。
 * 需要「仅小屏生效」的样式统一用 `html.app-compact xxx` 选择器，而不依赖 media query
 * （zoom 会改变 media query 的判定基准，此类才是紧凑模式的唯一可靠信号）。
 */
const COMPACT_CLASS = 'app-compact';

/** 当前是否处于小屏紧凑模式，供需要在脚本里区分的场景使用（如默认收起某块区域） */
export function isCompactDisplay(): boolean {
  return document.documentElement.classList.contains(COMPACT_CLASS);
}

/** 根据当前窗口尺寸应用或还原缩放，并同步紧凑模式标记类 */
function applyDisplayScale(): void {
  const compact =
    window.innerWidth <= SCALE_BREAKPOINT || window.innerHeight <= SCALE_HEIGHT_BREAKPOINT;
  const zoom = String(compact ? SCALE_RATIO : 1);
  document.documentElement.style.setProperty('zoom', zoom);
  // 供 --app-vh 换算真实一屏高度（zoom 下 100vh 只占视口的 zoom 倍）
  document.documentElement.style.setProperty('--app-zoom', zoom);
  document.documentElement.classList.toggle(COMPACT_CLASS, compact);
}

/** 初始化：立即应用一次，并监听窗口尺寸变化（防抖）动态切换 */
export function setupDisplayScale(): void {
  applyDisplayScale();

  let timer: ReturnType<typeof setTimeout> | null = null;
  window.addEventListener('resize', () => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(applyDisplayScale, RESIZE_DEBOUNCE);
  });
}
